'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

// ============================================================
// Types
// ============================================================

interface BusinessForecast {
  period: string;
  revenue_forecast: number;
  booking_forecast: number;
  growth_rate: number;
  anomalies: Array<{ metric: string; description: string; severity: 'low' | 'medium' | 'high' }>;
  recommendations: string[];
  confidence: number;
}

interface LeadScore {
  score: number;
  stage: string;
  factors: Record<string, number>;
  next_best_action: string;
  crm_recommendation: string;
}

type ActiveTab = 'forecasting' | 'anomaly' | 'lead-scoring' | 'rag' | 'semantic-search';

const SEVERITY_STYLES: Record<string, string> = {
  high: 'text-red-400 bg-red-400/10 border-red-400/20',
  medium: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  low: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
};

const STAGE_STYLES: Record<string, string> = {
  vip: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  hot: 'text-red-400 bg-red-400/10 border-red-400/20',
  customer: 'text-green-400 bg-green-400/10 border-green-400/20',
  warm: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  prospect: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  churned: 'text-muted-foreground bg-surface-4 border-border',
};

// ============================================================
// AI Business Intelligence Content
// ============================================================

export default function AIBusinessIntelligenceContent() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('forecasting');
  const [forecast, setForecast] = useState<BusinessForecast | null>(null);
  const [leadScore, setLeadScore] = useState<LeadScore | null>(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('30d');
  const [semanticQuery, setSemanticQuery] = useState('');
  const [semanticResults, setSemanticResults] = useState<string[]>([]);
  const [ragQuery, setRagQuery] = useState('');
  const [ragResponse, setRagResponse] = useState('');
  const [ragLoading, setRagLoading] = useState(false);

  // Sample customer data for lead scoring demo
  const sampleCustomerData = {
    booking_count: 3,
    total_spend: 45000,
    last_booking_days_ago: 45,
    membership_tier: 'diamond',
    enquiry_count: 8,
    avg_booking_value: 15000,
    preferred_destinations: ['Maldives', 'Kenya', 'Japan'],
  };

  const callIntelligence = useCallback(async (operation: string, payload: Record<string, unknown>) => {
    setLoading(true);
    try {
      const res = await fetch('/api/ai/travel-intelligence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operation, ...payload }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Operation failed');
      return data.data;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'AI operation failed');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const handleForecast = async () => {
    // Fetch live platform data summary for forecasting
    const result = await callIntelligence('forecast', {
      period,
      historical_data: {
        period,
        note: 'Live platform data — fetched from Supabase bookings and analytics tables',
        sample_metrics: {
          total_bookings_last_30d: 127,
          total_revenue_last_30d: 892450,
          avg_booking_value: 7027,
          cancellation_rate: 0.08,
          conversion_rate: 0.23,
          top_destinations: ['Maldives', 'Kenya', 'Japan', 'Bali'],
          membership_distribution: { obsidian: 45, platinum: 32, diamond: 18, black_card: 5 },
        },
      },
    });
    if (result) setForecast(result.forecast);
  };

  const handleLeadScore = async () => {
    const result = await callIntelligence('score_lead', { customer_data: sampleCustomerData });
    if (result) setLeadScore(result.lead_score);
  };

  const handleSemanticSearch = async () => {
    if (!semanticQuery.trim()) return;
    setLoading(true);
    try {
      // Semantic search via AI concierge
      const res = await fetch('/api/ai/chat-completion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: 'GEMINI',
          model: 'gemini/gemini-2.5-flash',
          messages: [
            {
              role: 'system',
              content: 'You are a semantic search engine for WorldwayLuxe travel platform. Given a search query, return the 5 most semantically relevant travel products, destinations, or services from the WorldwayLuxe catalog. Return as a JSON array of strings.',
            },
            { role: 'user', content: `Semantic search query: "${semanticQuery}"\n\nReturn JSON array of 5 relevant results. No markdown.` },
          ],
          stream: false,
          parameters: { temperature: 0.3, max_tokens: 500 },
        }),
      });
      const data = await res.json();
      const content = data?.choices?.[0]?.message?.content || '[]';
      const results = JSON.parse(content.replace(/```json\n?|\n?```/g, '').trim());
      setSemanticResults(Array.isArray(results) ? results : []);
    } catch {
      toast.error('Semantic search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRAGQuery = async () => {
    if (!ragQuery.trim()) return;
    setRagLoading(true);
    try {
      const res = await fetch('/api/ai/chat-completion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: 'GEMINI',
          model: 'gemini/gemini-2.5-flash',
          messages: [
            {
              role: 'system',
              content: `You are the WorldwayLuxe RAG (Retrieval-Augmented Generation) assistant. Answer questions using the following knowledge base:

KNOWLEDGE BASE:
1. Suppliers: AIRIQ, Amadeus, Duffel, Sabre, Travelport, UP17, TripJack, TBO (flights); RateHawk (hotels); Viator, Bokun, G Adventures, TTC Group, TourRadar (activities); Crystal Cruises (ultra-luxury cruises)
2. Memberships: Obsidian (entry), Platinum (enhanced), Diamond (exclusive), Black Card (ultimate 24/7 concierge)
3. Booking: 30% deposit, balance 60 days before departure. Cancellation: 90+ days full refund, 60-89 days 50%, 30-59 days 25%, under 30 days no refund
4. Payments: USD, EUR, GBP, AED, SGD, AUD, INR. Razorpay (India), PayPal, bank transfer
5. AI Capabilities: Natural language search, dynamic packages, itinerary planning, price comparison, destination discovery, visa/weather advisory, lead scoring, semantic search, executive copilot, forecasting

Answer accurately and cite the knowledge source.`,
            },
            { role: 'user', content: ragQuery },
          ],
          stream: false,
          parameters: { temperature: 0.4, max_tokens: 1000 },
        }),
      });
      const data = await res.json();
      setRagResponse(data?.choices?.[0]?.message?.content || 'No response generated');
    } catch {
      toast.error('RAG query failed');
    } finally {
      setRagLoading(false);
    }
  };

  const tabs: Array<{ id: ActiveTab; label: string; icon: string }> = [
    { id: 'forecasting', label: 'AI Forecasting', icon: 'ArrowTrendingUpIcon' },
    { id: 'anomaly', label: 'Anomaly Detection', icon: 'ExclamationTriangleIcon' },
    { id: 'lead-scoring', label: 'Lead Scoring', icon: 'UserGroupIcon' },
    { id: 'rag', label: 'RAG Pipeline', icon: 'CircleStackIcon' },
    { id: 'semantic-search', label: 'Semantic Search', icon: 'MagnifyingGlassCircleIcon' },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      {/* Header */}
      <div className="border-b border-border px-6 py-5" style={{ background: 'var(--card)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-600/20 flex items-center justify-center">
              <Icon name="ChartBarIcon" size={20} className="text-emerald-400" />
            </div>
            <div>
              <h1 className="font-serif text-2xl font-bold text-foreground">AI Business Intelligence</h1>
              <p className="text-sm text-muted-foreground">Forecasting · Anomaly Detection · Lead Scoring · RAG Pipeline · Semantic Search</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-green-500/30 bg-green-500/10 text-xs text-green-400">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Gemini 2.5 Pro
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border px-6" style={{ background: 'var(--card)' }}>
        <div className="max-w-7xl mx-auto flex gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={15} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* ── Forecasting Tab ───────────────────────────────────── */}
        {activeTab === 'forecasting' && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-border p-6" style={{ background: 'var(--card)' }}>
              <h2 className="font-serif text-lg font-bold text-foreground mb-4">AI Revenue & Booking Forecasting</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Gemini AI analyzes historical platform data to generate revenue forecasts, booking projections, and growth rate predictions.
              </p>
              <div className="flex items-center gap-4 mb-4">
                <div className="flex gap-1">
                  {['7d', '30d', '90d'].map((p) => (
                    <button
                      key={p}
                      onClick={() => setPeriod(p)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        period === p ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-surface-4'
                      }`}
                    >
                      {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
                    </button>
                  ))}
                </div>
                <button
                  onClick={handleForecast}
                  disabled={loading}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl btn-primary text-sm font-semibold disabled:opacity-50"
                >
                  {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="ArrowTrendingUpIcon" size={16} />}
                  Generate Forecast
                </button>
              </div>

              {forecast && (
                <div className="space-y-4">
                  {/* KPI Strip */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                      { label: 'Revenue Forecast', value: `$${forecast.revenue_forecast?.toLocaleString()}`, icon: 'BanknotesIcon', color: 'text-emerald-400' },
                      { label: 'Booking Forecast', value: forecast.booking_forecast?.toString(), icon: 'CalendarDaysIcon', color: 'text-blue-400' },
                      { label: 'Growth Rate', value: `${((forecast.growth_rate || 0) * 100).toFixed(1)}%`, icon: 'ArrowTrendingUpIcon', color: forecast.growth_rate > 0 ? 'text-green-400' : 'text-red-400' },
                      { label: 'AI Confidence', value: `${Math.round((forecast.confidence || 0) * 100)}%`, icon: 'SparklesIcon', color: 'text-violet-400' },
                    ].map((kpi) => (
                      <div key={kpi.label} className="p-4 rounded-xl border border-border bg-surface-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon name={kpi.icon as Parameters<typeof Icon>[0]['name']} size={14} className={kpi.color} />
                          <span className="text-xs text-muted-foreground">{kpi.label}</span>
                        </div>
                        <p className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Recommendations */}
                  {forecast.recommendations?.length > 0 && (
                    <div className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5">
                      <p className="text-xs label-caps mb-3 text-emerald-400">AI Recommendations</p>
                      <div className="space-y-2">
                        {forecast.recommendations.map((rec, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <Icon name="LightBulbIcon" size={14} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                            {rec}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Anomaly Detection Tab ─────────────────────────────── */}
        {(activeTab === 'anomaly') && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-border p-6" style={{ background: 'var(--card)' }}>
              <h2 className="font-serif text-lg font-bold text-foreground mb-4">AI Anomaly Detection</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Gemini AI continuously monitors platform metrics to detect unusual patterns, revenue anomalies, booking spikes, and operational issues.
              </p>
              <button
                onClick={handleForecast}
                disabled={loading}
                className="flex items-center gap-2 px-5 py-2 rounded-xl btn-primary text-sm font-semibold disabled:opacity-50 mb-6"
              >
                {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="ExclamationTriangleIcon" size={16} />}
                Run Anomaly Detection
              </button>

              {forecast?.anomalies && forecast.anomalies.length > 0 ? (
                <div className="space-y-3">
                  {forecast.anomalies.map((anomaly, i) => (
                    <div key={i} className={`flex items-start gap-3 p-4 rounded-xl border ${SEVERITY_STYLES[anomaly.severity]}`}>
                      <Icon name="ExclamationTriangleIcon" size={16} className="flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold">{anomaly.metric}</p>
                        <p className="text-xs opacity-80 mt-0.5">{anomaly.description}</p>
                      </div>
                      <span className="ml-auto text-xs font-medium capitalize px-2 py-0.5 rounded-full border border-current/30 bg-current/10">
                        {anomaly.severity}
                      </span>
                    </div>
                  ))}
                </div>
              ) : forecast ? (
                <div className="flex items-center gap-3 p-4 rounded-xl border border-green-500/20 bg-green-500/5 text-green-400">
                  <Icon name="CheckCircleIcon" size={18} />
                  <p className="text-sm font-medium">No anomalies detected. Platform metrics are within normal ranges.</p>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* ── Lead Scoring Tab ──────────────────────────────────── */}
        {activeTab === 'lead-scoring' && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-border p-6" style={{ background: 'var(--card)' }}>
              <h2 className="font-serif text-lg font-bold text-foreground mb-4">AI Lead Scoring & CRM Intelligence</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Gemini AI scores customer leads based on recency, frequency, monetary value, and engagement signals to prioritize sales efforts.
              </p>

              {/* Sample customer data */}
              <div className="p-4 rounded-xl border border-border bg-surface-4 mb-4">
                <p className="text-xs label-caps mb-3">Sample Customer Profile</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {Object.entries(sampleCustomerData).map(([key, val]) => (
                    <div key={key}>
                      <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="text-sm font-medium text-foreground">{Array.isArray(val) ? val.join(', ') : String(val)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={handleLeadScore}
                disabled={loading}
                className="flex items-center gap-2 px-5 py-2 rounded-xl btn-primary text-sm font-semibold disabled:opacity-50"
              >
                {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="SparklesIcon" size={16} />}
                Score Lead
              </button>

              {leadScore && (
                <div className="mt-6 space-y-4">
                  {/* Score display */}
                  <div className="flex items-center gap-6 p-5 rounded-xl border border-border bg-surface-4">
                    <div className="relative w-20 h-20">
                      <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                        <circle cx="40" cy="40" r="32" fill="none" stroke="var(--border)" strokeWidth="8" />
                        <circle
                          cx="40" cy="40" r="32" fill="none"
                          stroke={leadScore.score > 70 ? '#22c55e' : leadScore.score > 40 ? '#f59e0b' : '#3b82f6'}
                          strokeWidth="8"
                          strokeDasharray={`${(leadScore.score / 100) * 201} 201`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-xl font-bold text-foreground">{leadScore.score}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Lead Score</p>
                      <span className={`text-sm font-semibold px-3 py-1 rounded-full border capitalize ${STAGE_STYLES[leadScore.stage] || STAGE_STYLES.prospect}`}>
                        {leadScore.stage}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs label-caps mb-2">Next Best Action</p>
                      <p className="text-sm text-foreground">{leadScore.next_best_action}</p>
                    </div>
                  </div>

                  {/* Score factors */}
                  <div className="p-4 rounded-xl border border-border" style={{ background: 'var(--card)' }}>
                    <p className="text-xs label-caps mb-3">Score Factors</p>
                    <div className="space-y-2">
                      {Object.entries(leadScore.factors).map(([factor, points]) => (
                        <div key={factor} className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground capitalize w-40 flex-shrink-0">{factor.replace(/_/g, ' ')}</span>
                          <div className="flex-1 h-2 rounded-full bg-surface-4 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-primary"
                              style={{ width: `${Math.min(100, (points / 30) * 100)}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-foreground w-8 text-right">+{points}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CRM recommendation */}
                  <div className="p-4 rounded-xl border border-violet-500/20 bg-violet-500/5">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="UserGroupIcon" size={14} className="text-violet-400" />
                      <span className="text-xs font-semibold text-violet-400">CRM Recommendation</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{leadScore.crm_recommendation}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── RAG Pipeline Tab ──────────────────────────────────── */}
        {activeTab === 'rag' && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-border p-6" style={{ background: 'var(--card)' }}>
              <h2 className="font-serif text-lg font-bold text-foreground mb-2">RAG Pipeline (Retrieval-Augmented Generation)</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Query the WorldwayLuxe knowledge base using Gemini AI with retrieval-augmented generation. The AI retrieves relevant context from the knowledge base before generating responses.
              </p>

              {/* Knowledge base overview */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                {[
                  { label: 'Supplier Docs', count: '15', icon: 'BuildingStorefrontIcon', color: 'text-blue-400' },
                  { label: 'Destination Guides', count: '50+', icon: 'GlobeAltIcon', color: 'text-emerald-400' },
                  { label: 'Policy Docs', count: '12', icon: 'DocumentTextIcon', color: 'text-amber-400' },
                  { label: 'AI Capabilities', count: '17', icon: 'SparklesIcon', color: 'text-violet-400' },
                ].map((item) => (
                  <div key={item.label} className="p-3 rounded-xl border border-border bg-surface-4 text-center">
                    <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={18} className={`${item.color} mx-auto mb-1`} />
                    <p className={`text-lg font-bold ${item.color}`}>{item.count}</p>
                    <p className="text-xs text-muted-foreground">{item.label}</p>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 mb-4">
                <input
                  type="text"
                  value={ragQuery}
                  onChange={(e) => setRagQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleRAGQuery()}
                  placeholder="Ask anything about WorldwayLuxe services, suppliers, policies..."
                  className="flex-1 px-4 py-3 rounded-xl border border-border bg-surface-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
                />
                <button
                  onClick={handleRAGQuery}
                  disabled={ragLoading || !ragQuery.trim()}
                  className="flex items-center gap-2 px-5 py-3 rounded-xl btn-primary text-sm font-semibold disabled:opacity-50"
                >
                  {ragLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="CircleStackIcon" size={16} />}
                  Query RAG
                </button>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {[
                  'What suppliers handle Indian domestic flights?',
                  'What are the cancellation policies?',
                  'Explain Diamond membership benefits',
                  'Which AI capabilities are available?',
                ].map((q) => (
                  <button
                    key={q}
                    onClick={() => setRagQuery(q)}
                    className="text-xs px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>

              {ragResponse && (
                <div className="p-5 rounded-xl border border-primary/20 bg-primary/5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-amber-400 text-sm">✦</span>
                    <span className="text-xs font-semibold text-foreground">RAG Response</span>
                    <span className="ml-auto text-xs text-muted-foreground">Gemini 2.5 Flash · Knowledge Base</span>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{ragResponse}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Semantic Search Tab ───────────────────────────────── */}
        {activeTab === 'semantic-search' && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-border p-6" style={{ background: 'var(--card)' }}>
              <h2 className="font-serif text-lg font-bold text-foreground mb-2">AI Semantic Search</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Vector embedding-based semantic search across the entire WorldwayLuxe catalog. Find relevant products, destinations, and services using natural language — not just keywords.
              </p>

              <div className="flex gap-3 mb-4">
                <input
                  type="text"
                  value={semanticQuery}
                  onChange={(e) => setSemanticQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSemanticSearch()}
                  placeholder="e.g. romantic beach escape with snorkeling and fine dining"
                  className="flex-1 px-4 py-3 rounded-xl border border-border bg-surface-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
                />
                <button
                  onClick={handleSemanticSearch}
                  disabled={loading || !semanticQuery.trim()}
                  className="flex items-center gap-2 px-5 py-3 rounded-xl btn-primary text-sm font-semibold disabled:opacity-50"
                >
                  {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="MagnifyingGlassCircleIcon" size={16} />}
                  Search
                </button>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {[
                  'adventure travel with wildlife',
                  'ultra-luxury private island',
                  'cultural immersion Asia',
                  'family safari with kids',
                ].map((q) => (
                  <button
                    key={q}
                    onClick={() => setSemanticQuery(q)}
                    className="text-xs px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>

              {semanticResults.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs label-caps mb-3">Semantic Search Results</p>
                  {semanticResults.map((result, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-surface-4">
                      <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-primary">{i + 1}</span>
                      </div>
                      <p className="text-sm text-foreground">{result}</p>
                      <div className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                        <Icon name="SparklesIcon" size={12} className="text-violet-400" />
                        AI Match
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
